import { addToast } from '@heroui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../constants';
import { useAuth } from '../useAuth';
import dashboardController, {
  SalesDashboardOrganization,
  SalesDashboardOrganizationCreditsUpdate,
} from '../../services/api/dashboardController';

type AuthLike = { tokens?: { idToken?: { jwtToken?: string } } };

export const useUpdateSalesOrganizationCredits = () => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const authToken = (auth as AuthLike)?.tokens?.idToken?.jwtToken ?? '';

  return useMutation({
    mutationFn: ({ orgId, credits }: { orgId: string; credits: number }) =>
      dashboardController.updateSalesOrganizationCredits(
        orgId,
        credits,
        authToken,
      ),
    onSuccess: (response) => {
      const updatedOrganization = (
        response as {
          data?: {
            organization?: SalesDashboardOrganizationCreditsUpdate;
          };
        }
      )?.data?.organization;

      if (!updatedOrganization?.orgUuid) {
        return;
      }

      queryClient.setQueryData<SalesDashboardOrganization[]>(
        QUERY_KEYS.SALES_ORGANIZATIONS,
        (organizations = []) =>
          organizations.map((organization) =>
            organization.orgUuid === updatedOrganization.orgUuid
              ? {
                  ...organization,
                  credits: updatedOrganization.credits,
                }
              : organization,
          ),
      );

      addToast({
        title: 'Credits updated',
        description: `${updatedOrganization.organisationName} now has ${updatedOrganization.credits} credits.`,
        color: 'success',
        severity: 'success',
        timeout: 2500,
      });
    },
    onError: () => {
      addToast({
        title: 'Failed to update credits',
        description: 'Check the value and try again.',
        color: 'danger',
        severity: 'danger',
        timeout: 3000,
      });
    },
  });
};
